import React, { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'

import { NotificationMenu } from '@app/components/NotificationMenu'
import { ExtendedLink } from '@app/components/ExtendedLink'

import { useJwtSession, useUserQuery } from '@app/hooks'
import { Icon, Show } from '@app/components/util'
import { mapRoleToPosition, getFullNameFromUser } from '@app/utils'

import { ICON_NAME } from '@app/constants/icon'
import { ICON_SIZE } from '@app/constants/button'
import { AUTH_PAGE, SETTINGS_PAGE } from '@app/constants/page'
import { PERMISSION, ROLE } from '@app/constants/permission'
import { AUTH_QUERY_KEY } from '@app/constants/queryKeys'
import authReturnUrlService from '@app/services/authReturnUrlService'

import { adaptDropdownMenu } from './lib'
import { Control, SettingsMenu, AccountInfo } from './ui'

import styles from './styles.module.scss'

import type { HeaderProps, SettingMenuItem, UserInfo } from './types'
import type { LntDropdownMenu, LntDropdownMenuItem } from '@app/components/base/Dropdown/types'

const logoutMenuItem: LntDropdownMenuItem = {
  key: 'logout',
  label: 'Выйти',
  icon: (
    <Icon
      name={ ICON_NAME.logout }
      size={ ICON_SIZE }
    />
  )
}

const rawSettingsMenuItems: SettingMenuItem[] = [
  {
    key: SETTINGS_PAGE.monitoringSchedule,
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.monitoringSchedule }>Расписание мониторингов</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.directoryContractors,
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.directoryContractors }>Справочник подрядчиков</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.managingSubtypes,
    disabled: true,
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.managingSubtypes }>Управление подтипами в шаблоне</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.managingMonitorings,
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.managingMonitorings }>Активные мониторинги</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.templatesEditor,
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.templatesEditor }>Редактор шаблонов</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.checkingPricesCompetitors,
    requiredRoles: [ROLE.pricingSpecialist],
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.checkingPricesCompetitors }>Проверка цен конкурентов</ExtendedLink>
  },
  {
    key: SETTINGS_PAGE.classifierControls,
    requiredRoles: [ROLE.pricingSpecialist],
    requiredPermissions: [PERMISSION.configureMonitoringParameters],
    label: <ExtendedLink to={ SETTINGS_PAGE.classifierControls }>Управление классификатором</ExtendedLink>
  }
]

export const Header: React.FC<HeaderProps> = ({ className: outerClassName, controlProps }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: user } = useUserQuery()
  const { session, clearSession } = useJwtSession()

  const userInfo = useMemo<UserInfo | null>(() => {
    if (!user) return null

    return { fullName: getFullNameFromUser(user), position: mapRoleToPosition(user.roles?.[0]) }
  }, [user])

  const settings = useMemo<LntDropdownMenu>(() => {
    return adaptDropdownMenu(rawSettingsMenuItems, user)
  }, [user])

  const handleLogout = async (): Promise<void> => {
    authReturnUrlService.clearReturnUrl()
    clearSession()
    queryClient.removeQueries({ queryKey: [AUTH_QUERY_KEY.authUser], exact: true })
    navigate(AUTH_PAGE.auth)
  }

  const accountInfoMenu: LntDropdownMenu = [
    {
      ...logoutMenuItem,
      onClick: handleLogout
    }
  ]

  const className: string = classNames(styles.header, outerClassName)

  return (
    <header className={ className }>
      <Control { ...controlProps } />

      <div className={ styles.header__toolbar }>
        <SettingsMenu settings={ settings } />

        <NotificationMenu />
      </div>

      <Show condition={ !!session }>
        <AccountInfo
          { ...userInfo }
          accountInfoMenu={ accountInfoMenu }
        />
      </Show>
    </header>
  )
}
